/**
 * SIMULATED AI extraction — stand-in for the real backend model.
 *
 * Reads the actual uploaded workbook and produces the normalized result the
 * confirmation/"training" screen renders: relevant tab, skipped tabs, detected
 * layout, and the extracted components -> materials (with weight vs % settled).
 * The real model replaces this function; the shape it returns stays the same.
 */
import {
  guessRelevantSheetName,
  guessHeaderRowIndex,
  isLikelyNoiseSheet,
  columnsFromHeaderRow
} from "./workbookParser";

/**
 * Composition text -> emission-factor material reference. Values are valid ids
 * from @aclymatepackages/lists productMaterials. Longer keys are checked first
 * (see resolveMaterialType) so e.g. "recycled polyester" wins over "polyester".
 */
const compositionMaterialSynonyms = {
  "acrylonitrile butadiene styrene": "abs",
  "recycled polyester": "rpet_textile",
  "recycled polypropylene": "rpp_plastic",
  "recycled stainless steel": "rsteel",
  "recycled stainless": "rsteel",
  "recycled iron": "recycled_iron",
  "recycled cotton": "recycled_cotton",
  "ps plastic": "gpps",
  "polystyrene plastic": "gpps",
  "polystyrene": "gpps",
  "pcr polycarbonate": "pcr_plastic",
  "pcr plastic": "pcr_plastic",
  polycarbonate: "pcr_plastic",
  pcr: "pcr_plastic",
  "zinc alloy": "zinc_alloy",
  "pp plastic": "pp_plastic",
  "pp polypropylene": "pp_plastic",
  "polypropylene non-woven": "pp_plastic",
  "pe plastic": "pe_plastic",
  "pe polyethylene": "pe_plastic",
  "pet plastic": "pet_plastic",
  "stainless steel": "stainless_steel",
  "carbon steel": "carbon_steel",
  "natural rubber": "natural_rubber",
  "synthetic rubber": "synthetic_rubber",
  "foam rubber": "foam_rubber",
  "iron alloy": "iron_alloy",
  polyester: "polyester_textile",
  polypropylene: "pp_plastic",
  polyethylene: "pe_plastic",
  polyamide: "nylon_plastic",
  polyoxymethylene: "pom",
  polyurethane: "rigid_polyurethane",
  cotton: "cotton",
  nylon: "nylon_plastic",
  rayon: "viscose",
  viscose: "viscose",
  wool: "wool",
  elastane: "elastane",
  spandex: "spandex",
  neoprene: "neoprene",
  silicone: "synthetic_rubber",
  silicon: "silicon",
  aluminium: "aluminium",
  aluminum: "aluminium",
  zinc: "zinc",
  iron: "iron",
  steel: "steel",
  brass: "brass",
  copper: "copper",
  tin: "tin",
  metal: "steel",
  rubber: "synthetic_rubber",
  abs: "abs",
  ldpe: "ldpe",
  hdpe: "hdpe",
  pom: "pom",
  tpu: "tpu",
  tpe: "tpe",
  eva: "eva",
  pvc: "pvc",
  pet: "pet_plastic",
  paper: "paper",
  cardboard: "cardboard",
  leather: "leather",
  bamboo: "bamboo",
  glass: "glass"
};

const cell = (row, index) => String(row?.[index] ?? "").trim();
const isBlank = (value) => String(value).trim() === "";
const isNumericLike = (value) => /^-?\d+\.?\d*$/.test(String(value).trim());

const convertToGrams = (value) => {
  const text = String(value || "").toLowerCase();
  const num = parseFloat(text.replace(/[^\d.-]/g, ""));
  if (!Number.isFinite(num)) {
    return null;
  }
  if (/kgs?/.test(text)) {
    return num * 1000;
  }
  if (/oz/.test(text)) {
    return num * 28.3495;
  }
  if (/lbs?|pounds?/.test(text)) {
    return num * 453.592;
  }
  return num;
};

const trimOrNull = (value) => {
  const text = String(value || "").trim();
  return text === "" ? null : text;
};

const PRODUCT_FIELD_PATTERNS = [
  {
    key: "sku",
    rx: /^(modelcode|model\s*code|sku|item\s*#|style\s*#|product\s*code)\s*$/i,
    cast: trimOrNull
  },
  {
    key: "name",
    rx: /^(name|product\s*name|description|item\s*name)\s*$/i,
    cast: trimOrNull
  },
  {
    key: "countryOfOrigin",
    rx: /^(country\s*of\s*origin|made\s*in|country)\s*$/i,
    cast: trimOrNull
  },
  {
    key: "cityOfOrigin",
    rx: /^(city|city\s*of\s*manufacture|manufacturer\s*city|town)\s*$/i,
    cast: trimOrNull
  },
  {
    key: "totalWeightGrams",
    rx: /^(?:total\s*weight\s*product\s*\+?\s*packaging|total\s*weight\s*product\s*and\s*packaging|gross\s*weight|total\s*weight)(?:\s*\(.*\)?)?\s*$/i,
    cast: convertToGrams
  },
  {
    key: "productWeightGrams",
    rx: /^(nett?\s*weight\s*product(?:\s*\(.*\))?|product\s*net\s*weight|net\s*weight\s*\(?product\)?|product\s*weight)\s*$/i,
    cast: convertToGrams
  },
  {
    key: "packagingWeightGrams",
    rx: /^(nett?\s*weight\s*packaging(?:\s*\(.*\))?|packaging\s*net\s*weight|net\s*weight\s*\(?packaging\)?|packaging\s*weight)\s*$/i,
    cast: convertToGrams
  }
];

const extractProductFromVertical = (rows, labelCol = 1, valueCol = 2) =>
  rows.reduce((acc, row) => {
    const label = cell(row, labelCol);
    const value = cell(row, valueCol);
    if (!label || !value) {
      return acc;
    }
    const match = PRODUCT_FIELD_PATTERNS.find(({ rx, key }) => {
      if (acc[key] != null) {
        return false;
      }
      return rx.test(label);
    });
    if (!match) {
      return acc;
    }
    const casted = match.cast(value);
    if (casted == null) {
      return acc;
    }
    return { ...acc, [match.key]: casted };
  }, {});

/**
 * Map a free-text material/composition to a known emission-factor reference.
 * Returns { type, confident } — type null means we couldn't resolve it (red).
 *
 * If a template captured user-confirmed picks for this format, those overrides
 * win — the user has trained us once, every future file of this format inherits
 * the mapping.
 */
export const resolveMaterialType = (text, overrides = {}) => {
  const lower = String(text).toLowerCase();
  if (overrides[lower]) {
    return { type: overrides[lower], confident: true };
  }
  const match = Object.keys(compositionMaterialSynonyms).find((word) =>
    lower.includes(word)
  );
  return match
    ? { type: compositionMaterialSynonyms[match], confident: true }
    : { type: null, confident: false };
};

const flagFor = ({ type, weight, percent }) => {
  if (!type) {
    return "insufficient";
  }
  if (weight == null && percent == null) {
    return "assumption";
  }
  return "reported";
};

const buildMaterial = ({ name, weight, percent }, overrides) => {
  const { type } = resolveMaterialType(name, overrides);
  return {
    rawName: name,
    materialType: type,
    weight: weight ?? null,
    percent: percent ?? null,
    valueKind: percent != null ? "percentage" : weight != null ? "weight" : "none",
    flag: flagFor({ type, weight, percent })
  };
};

const looksVertical = (rows) => {
  const headerJoined = (rows[0] || []).join(" ").toLowerCase();
  if (/data points|entry field/.test(headerJoined)) {
    return true;
  }
  const labelColumnSignal = rows
    .slice(0, 40)
    .filter((row) => /component|material/i.test(cell(row, 1))).length;
  return labelColumnSignal >= 2;
};

export const parsePercent = (value) => {
  const text = String(value).replace("%", "").trim();
  if (text === "" || !isNumericLike(text)) {
    return null;
  }
  return Number(text);
};

const parseWeightGrams = (value) => {
  const text = String(value).replace(/[^\d.]/g, "");
  const num = parseFloat(text);
  return Number.isFinite(num) ? num : null;
};

/**
 * Vertical key/value sheets (PCNA "Workfile"): a label column + a value column,
 * with components/materials spread down the rows as "Component N" / "Material N"
 * / "Material N percentage" / "Material N weight grms". This is the case manual
 * column-mapping can't do.
 */
const extractVertical = (rows, overrides) => {
  const labelCol = 1;
  const valueCol = 2;
  const components = [];

  rows.forEach((row) => {
    const label = cell(row, labelCol).toLowerCase();
    const value = cell(row, valueCol);

    if (/^component\s*\d*/.test(label)) {
      components.push({
        name: value || `Component ${components.length + 1}`,
        section: guessSection(value),
        componentWeightGrams: null,
        materials: []
      });
      return;
    }
    const current = components[components.length - 1];
    if (!current) {
      return;
    }
    if (/material\s*\d*\s*weight/.test(label)) {
      const weight = parseWeightGrams(value);
      if (weight != null) {
        current.componentWeightGrams = weight;
      }
      return;
    }
    if (/material\s*\d*\s*percentage/.test(label)) {
      const percent = parsePercent(value);
      if (percent == null) {
        return;
      }
      const lastMaterial = current.materials[current.materials.length - 1];
      if (lastMaterial) {
        lastMaterial.percent = percent;
        if (lastMaterial.weight == null) {
          lastMaterial.valueKind = "percentage";
        }
        lastMaterial.flag = flagFor({
          type: lastMaterial.materialType,
          weight: lastMaterial.weight,
          percent
        });
      }
      return;
    }
    if (/^material\s*\d*$/.test(label) && !isBlank(value)) {
      const lastMaterial = current.materials[current.materials.length - 1];
      const sameAsLast =
        lastMaterial &&
        lastMaterial.rawName.trim().toLowerCase() ===
          value.trim().toLowerCase();
      if (sameAsLast) {
        return;
      }
      current.materials.push(buildMaterial({ name: value }, overrides));
    }
  });

  return distributeComponentWeights(components).filter(
    ({ materials }) => materials.length
  );
};

const distributeComponentWeights = (components) =>
  components.map((component) => {
    const { componentWeightGrams, materials } = component;
    if (componentWeightGrams == null || !materials.length) {
      return component;
    }
    const allHaveWeights = materials.every(({ weight }) => weight != null);
    if (allHaveWeights) {
      return component;
    }
    if (materials.length === 1) {
      const [single] = materials;
      if (single.weight == null) {
        return {
          ...component,
          materials: [
            {
              ...single,
              weight: componentWeightGrams,
              valueKind: "weight",
              weightSource: "derived-from-component-weight",
              flag: flagFor({
                type: single.materialType,
                weight: componentWeightGrams,
                percent: single.percent
              })
            }
          ]
        };
      }
      return component;
    }
    const withPercent = materials.filter(({ percent }) => percent != null);
    const withoutPercent = materials.filter(({ percent }) => percent == null);
    const totalSpecifiedPercent = withPercent.reduce(
      (sum, { percent }) => sum + percent,
      0
    );
    if (!withPercent.length || totalSpecifiedPercent <= 0) {
      return component;
    }
    const usingDecimalShares = totalSpecifiedPercent <= 1.5;
    const fullPercent = usingDecimalShares ? 1 : 100;
    const remainderPercent = Math.max(
      fullPercent - totalSpecifiedPercent,
      0
    );
    const remainderShare =
      withoutPercent.length > 0 ? remainderPercent / withoutPercent.length : 0;
    const updatedMaterials = materials.map((material) => {
      if (material.weight != null) {
        return material;
      }
      const share =
        material.percent != null
          ? material.percent / fullPercent
          : remainderShare / fullPercent;
      if (share <= 0) {
        return material;
      }
      const weight = componentWeightGrams * share;
      return {
        ...material,
        weight,
        valueKind: "weight",
        weightSource: "derived-from-component-weight",
        flag: flagFor({
          type: material.materialType,
          weight,
          percent: material.percent
        })
      };
    });
    return { ...component, materials: updatedMaterials };
  });

const findRole = (columns, keywords) =>
  columns.find(({ label }) =>
    keywords.some((word) => label.toLowerCase().includes(word))
  )?.index;

// Section labels the confirmation screen offers. "Packaging" is the one that
// matters downstream: it moves a component out of the Components tab and into
// the Packaging tab (see writeProductDocs in bomBulkUploadTasks.js).
export const COMPONENT_SECTIONS = ["Fabrics", "Trims", "Labels", "Packaging"];

const PACKAGING_NAME_HINT =
  /(packag|poly\s?bag|carton|\bbox\b|pouch|bubble\s?wrap|shipping\s?bag|mailer)/i;

// "Packaging (3)" -> "Packaging", mirroring the server parser's
// stripSectionCount so both sides agree on the label.
const cleanSectionLabel = (label) => {
  const stripped = String(label || "")
    .replace(/\s*\(\s*\d+\s*\)\s*$/, "")
    .trim();
  // "PACKAGING" / "packaging" -> "Packaging", so casing in the file never
  // changes how a section is classified or which dropdown option it selects.
  return (
    COMPONENT_SECTIONS.find(
      (section) => section.toLowerCase() === stripped.toLowerCase()
    ) || stripped
  );
};

// A section header row in the file wins. Without one, a component whose name
// clearly reads as packaging is pre-filled as Packaging. Either way the user
// confirms or changes it on the confirmation screen -- this only pre-fills.
export const guessSection = (componentName, sectionFromFile = null) => {
  if (sectionFromFile) {
    return sectionFromFile;
  }
  return PACKAGING_NAME_HINT.test(componentName || "") ? "Packaging" : null;
};

// Walks the data rows once, carrying the latest section header row forward.
// A section header row has a name and nothing else -- checked against the
// detected name column, not a fixed column A.
const rowsWithSections = (rows, headerRowIndex, nameCol) =>
  rows.slice(headerRowIndex + 1).reduce(
    (acc, row) => {
      const name = cell(row, nameCol);
      const isSectionRow =
        name && row.every((value, index) => index === nameCol || isBlank(value));
      if (isSectionRow) {
        return { ...acc, currentSection: cleanSectionLabel(name) || null };
      }
      if (!name) {
        return acc;
      }
      return {
        ...acc,
        items: [
          ...acc.items,
          {
            row,
            name,
            section: guessSection(name, acc.currentSection)
          }
        ]
      };
    },
    { currentSection: null, items: [] }
  ).items;

/**
 * Tabular sheets (Storm Creek): one row per material; columns are fields.
 */
const extractTabular = (rows, headerRowIndex, overrides) => {
  const columns = columnsFromHeaderRow(rows[headerRowIndex] || []);
  const nameCol = findRole(columns, ["product", "component", "item", "name"]) ?? 0;
  const compositionCol = findRole(columns, ["composition", "material", "content"]);
  const weightCol = findRole(columns, ["total mass", "weight", "mass"]);

  return rowsWithSections(rows, headerRowIndex, nameCol).map(
    ({ row, name, section }) => {
      const composition =
        compositionCol != null ? cell(row, compositionCol) : "";
      const weightRaw = weightCol != null ? cell(row, weightCol) : "";
      const weight = isNumericLike(weightRaw) ? Number(weightRaw) : null;
      return {
        name,
        section,
        materials: [buildMaterial({ name: composition || name, weight }, overrides)]
      };
    }
  );
};

/**
 * Tabular BOMs report origin per material row (country / city / shipping
 * port), not once for the whole product like vertical layouts do. Mirrors the
 * server-side derivation in parseFromTemplate.js: the first row outside the
 * Packaging section that reports any origin field wins (packaging is made
 * elsewhere), falling back to the first row with any origin at all. The
 * shipping port is preferred downstream over city for geocoding.
 */
export const extractTabularOrigin = (rows, headerRowIndex) => {
  const columns = columnsFromHeaderRow(rows[headerRowIndex] || []);
  const nameCol = findRole(columns, ["product", "component", "item", "name"]) ?? 0;
  const countryCol = findRole(columns, ["country"]);
  const cityCol = findRole(columns, ["city"]);
  const portCol = findRole(columns, ["port"]);
  const valueAt = (row, col) => (col != null ? trimOrNull(cell(row, col)) : null);

  const origins = rowsWithSections(rows, headerRowIndex, nameCol)
    .map(({ row, section }) => ({
      countryOfOrigin: valueAt(row, countryCol),
      cityOfOrigin: valueAt(row, cityCol),
      portOfOrigin: valueAt(row, portCol),
      isPackaging: section === "Packaging"
    }))
    .filter(
      ({ countryOfOrigin, cityOfOrigin, portOfOrigin }) =>
        countryOfOrigin || cityOfOrigin || portOfOrigin
    );

  const { isPackaging: _ignored, ...chosen } =
    origins.find(({ isPackaging }) => !isPackaging) || origins[0] || {};
  return chosen;
};

/**
 * The column roles the tabular extractor above actually used, as the header
 * labels it matched. Saved with the format so the server-side template parser
 * reads the same columns on every later file (it finds columns only through a
 * template's columnRoles; a role-less saved format would parse nothing but
 * the name column). Labels are lowercased because the server matches by
 * substring against lowercased headers.
 */
export const detectTabularColumnRoles = (rows, headerRowIndex) => {
  const columns = columnsFromHeaderRow(rows[headerRowIndex] || []);
  const labelAt = (index) =>
    index != null ? [columns[index].label.toLowerCase()] : [];
  const roles = {
    name: labelAt(findRole(columns, ["product", "component", "item", "name"])),
    composition: labelAt(
      findRole(columns, ["composition", "material", "content"])
    ),
    weight: labelAt(findRole(columns, ["total mass", "weight", "mass"])),
    country: labelAt(findRole(columns, ["country"])),
    city: labelAt(findRole(columns, ["city"])),
    port: labelAt(findRole(columns, ["port"]))
  };
  return Object.fromEntries(
    Object.entries(roles).filter(([, labels]) => labels.length)
  );
};

export const simulateExtraction = ({ fileName, sheets }, options = {}) => {
  const { overrides = {} } = options;

  const skippedSheets = sheets
    .filter(({ name }) => isLikelyNoiseSheet(name))
    .map(({ name }) => name);

  const relevantSheetName = guessRelevantSheetName(sheets);
  const sheet = sheets.find(({ name }) => name === relevantSheetName) || sheets[0];
  const rows = sheet?.rows || [];

  const layout = looksVertical(rows) ? "key-value" : "tabular";
  const headerRowIndex = guessHeaderRowIndex(rows);

  const components =
    layout === "key-value"
      ? extractVertical(rows, overrides)
      : extractTabular(rows, headerRowIndex, overrides);

  const filenameProductName = fileName.replace(/\.[^.]+$/, "").replace(/_/g, " ");
  const productFromRows =
    layout === "key-value"
      ? extractProductFromVertical(rows)
      : extractTabularOrigin(rows, headerRowIndex);

  return {
    fileName,
    product: {
      name: productFromRows.name || filenameProductName,
      sku: productFromRows.sku || null,
      category: null,
      subcategory: null,
      countryOfOrigin: productFromRows.countryOfOrigin || null,
      cityOfOrigin: productFromRows.cityOfOrigin || null,
      portOfOrigin: productFromRows.portOfOrigin || null,
      productWeightGrams: productFromRows.productWeightGrams ?? null,
      packagingWeightGrams: productFromRows.packagingWeightGrams ?? null,
      totalWeightGrams: productFromRows.totalWeightGrams ?? null
    },
    relevantSheet: relevantSheetName,
    skippedSheets,
    layout,
    headerRowIndex,
    ...(layout === "tabular"
      ? { columnRoles: detectTabularColumnRoles(rows, headerRowIndex) }
      : {}),
    components
  };
};

export const summarizeExtraction = (extraction) => {
  const materials = extraction.components.flatMap((c) => c.materials);
  const countFlag = (flag) => materials.filter((m) => m.flag === flag).length;
  return {
    componentCount: extraction.components.length,
    materialCount: materials.length,
    reportedCount: countFlag("reported"),
    assumptionCount: countFlag("assumption"),
    insufficientCount: countFlag("insufficient")
  };
};
